import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
    Header,
    PersonalInfoForm,
    AccountCard,
    ContainedButton,
    OutlinedButton,
    Progress,
    Text,
} from '../index';

import { StyledCard, StyledCardContent } from './style';

import { GET_PATIENT_PROFILE, UPDATE_PATIENT_PROFILE } from '../../services/patient';
import { resetPassword } from '../../services/index';
import { validEmail, validPhone, specialCharacters, validPassword } from '../../utils/regex';
import { alertError, alertSuccess } from '../../redux/stateActions';

export const EditProfileCard = ({ mdUp, onGoBack }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [errorsPasswordForm, setErrorsPasswordForm] = useState([]);
    const [errorsAccountForm, setErrorsAccountForm] = useState([]);
    const [inputValue, setInputValue] = useState({
        id: `${user.id}`,
        fullname: '',
        gender: '',
        email: '',
        birthday: '',
        mobileNumber: '',
    });

    const [passwordInput, setPasswordInput] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    //  Patient Personal Profile
    const {
        data: getPatientData,
        loading: getPatientLoading,
        error: getPatientError,
        refetch: getPatientRefetch,
    } = useQuery(GET_PATIENT_PROFILE, {
        variables: { id: user.id },
    });

    useEffect(() => {
        if (getPatientError) {
            dispatch(alertError(`cannot get Client: ${getPatientError.message}`));
        }
        if (getPatientData) {
            setInputValue(getPatientData.user);
        }
    }, [getPatientError, getPatientData]);

    const [
        updatePatientProfile,
        { data: updatePatientData, loading: updatePatientLoading, error: updatePatientError },
    ] = useMutation(UPDATE_PATIENT_PROFILE);

    useEffect(() => {
        if (updatePatientError) {
            dispatch(alertError(`Cannot update Client: ${updatePatientError.message}`));
        }
        if (updatePatientData) {
            dispatch(alertSuccess('Client Profile Updated'));
            getPatientRefetch();
        }
    }, [updatePatientError, updatePatientData]);

    const handleDateChange = (event) => {
        let newErrors = [...errorsAccountForm];

        const newAccount = { ...inputValue };

        if (event === null) {
            newAccount.birthday = null;
            setInputValue(newAccount);

            newErrors = newErrors.filter((error) => error.property !== 'birthday');
        } else if (dayjs(event).isValid()) {
            newAccount.birthday = event.toISOString();
            setInputValue(newAccount);

            newErrors = newErrors.filter((error) => error.property !== 'birthday');
        } else {
            newErrors.push({
                property: 'birthday',
                message: 'Invalid date.',
            });
        }

        setErrorsAccountForm(newErrors);
    };

    const handleBirthdayValidationError = (reason) => {
        setErrorsAccountForm((prev) => {
            const hasBirthdayError = prev.some((e) => e.property === 'birthday');
            if (reason) {
                if (hasBirthdayError) return prev;
                return [...prev, { property: 'birthday', message: reason }];
            }
            return prev;
        });
    };

    // Personal Account Change
    const handleAccountChange = (event) => {
        const newAccount = { ...inputValue };
        const name = event.target.name;
        const value = event.target.value;

        newAccount[name] = value;

        setInputValue(newAccount);

        setErrorsAccountForm(errorsAccountForm.filter((error) => error.property !== name));
    };

    // Password Set Up
    const handlePasswordChange = (event) => {
        const newPassword = { ...passwordInput };
        const name = event.target.name;
        const value = event.target.value;

        newPassword[name] = value;
        setPasswordInput(newPassword);

        setErrorsPasswordForm(errorsPasswordForm.filter((error) => error.property !== name));
    };

    //Personal Account Reset
    const handleAccountReset = () => {
        setErrorsAccountForm((errors) =>
            errors.filter((err) =>
                ['oldPassword', 'newPassword', 'confirmPassword'].includes(err.property)
            )
        );
        const now = dayjs();
        setInputValue({
            ...getPatientData.user,
            birthday: getPatientData.user.birthday
                ? dayjs(getPatientData.user.birthday).millisecond(now.millisecond()).toISOString()
                : null,
        });
    };

    const handlePasswordReset = () => {
        setErrorsPasswordForm((errors) =>
            errors.filter(
                (err) => !['oldPassword', 'newPassword', 'confirmPassword'].includes(err.property)
            )
        );
        setPasswordInput({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
    };

    const handlePasswordSave = async () => {
        const newErrors = [];

        if (passwordInput.oldPassword === '') {
            newErrors.push({
                property: 'oldPassword',
                message: 'Please provide old password.',
            });
        }

        if (passwordInput.newPassword === '') {
            newErrors.push({
                property: 'newPassword',
                message: 'Please provide new password.',
            });
        }

        if (!validPassword.test(passwordInput.newPassword)) {
            newErrors.push({
                property: 'newPassword',
                message: 'Password must be at least 8 character and special character and number.',
            });
        }

        if (passwordInput.oldPassword === passwordInput.newPassword) {
            newErrors.push({
                property: 'newPassword',
                message: 'New password must differ from old password.',
            });
        }

        if (passwordInput.confirmPassword === '') {
            newErrors.push({
                property: 'confirmPassword',
                message: 'Please provide confirm password.',
            });
        }

        if (passwordInput.newPassword != passwordInput.confirmPassword) {
            newErrors.push({
                property: 'confirmPassword',
                message: 'password does not match',
            });
        }

        if (newErrors.length)
            return setErrorsPasswordForm((errors) => [
                ...errors.filter(
                    (err) =>
                        !['oldPassword', 'newPassword', 'confirmPassword'].includes(err.property)
                ),
                ...newErrors,
            ]);

        //connect to strapi & check password
        try {
            const res = await resetPassword({
                oldPassword: `${passwordInput.oldPassword}`,
                newPassword: `${passwordInput.newPassword}`,
                user_id: user.id,
                notificationOwner: user.patient.id,
            });
            if (!res) {
                throw new Error(res);
            }
            dispatch(alertSuccess("Client's password updated!"));
            setErrorsPasswordForm([]);
        } catch (error) {
            dispatch(alertError(error?.response?.data?.message));
        }
        setPasswordInput({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
    };

    const handleAccountSave = () => {
        const newErrors = [];

        if (inputValue.fullname?.trim() === '') {
            newErrors.push({
                property: 'fullname',
                message: 'Please provide a fullname.',
            });
        }

        if (specialCharacters.test(inputValue.fullname)) {
            newErrors.push({
                property: 'fullname',
                message: 'Fullname must not include special characters.',
            });
        }

        if (!validEmail.test(inputValue.email)) {
            newErrors.push({
                property: 'email',
                message: 'Please provide a valid email.',
            });
        }

        if (!validPhone.test(inputValue.mobileNumber)) {
            newErrors.push({
                property: 'mobileNumber',
                message: 'Please provide a Mobile Number.',
            });
        }

        if (newErrors.length)
            setErrorsAccountForm((errors) => [
                ...errors.filter(
                    (err) => !['fullname', 'email', 'mobileNumber'].includes(err.property)
                ),
                ...newErrors,
            ]);


        if (errorsAccountForm.length || newErrors.length) return;

        updatePatientProfile({
            variables: {
                ...inputValue,
                birthday: inputValue.birthday
                    ? dayjs(inputValue.birthday).format('YYYY-MM-DD')
                    : null,
            },
        });
    };

    return (
        <div>
            {!mdUp && (
                <Header
                    onClickGoBack={onGoBack}
                    showGoBack1
                    childrenContainerStyle={{ textAlign: 'center' }}
                >
                    <Text>Edit Profile</Text>
                </Header>
            )}
            {updatePatientLoading || (getPatientLoading && <Progress />)}
            <div>
                <AccountCard
                    data={passwordInput}
                    onChange={handlePasswordChange}
                    errors={errorsPasswordForm}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        marginTop: '20px',
                        marginBottom: '25px',
                    }}
                >
                    <OutlinedButton $width="96px" $height="32px" onClick={handlePasswordReset}>
                        Clear
                    </OutlinedButton>
                    <ContainedButton
                        $width="96px"
                        $height="32px"
                        style={{ marginLeft: '15px' }}
                        onClick={handlePasswordSave}
                    >
                        Save
                    </ContainedButton>
                </div>
            </div>
            <div>
                <StyledCard variant="outlined" padding="20px">
                    <StyledCardContent>
                        <PersonalInfoForm
                            data={inputValue}
                            onChange={handleAccountChange}
                            onDateChange={handleDateChange}
                            onValidationError={handleBirthdayValidationError}
                            errors={errorsAccountForm}
                            disabledHNCode
                            disabledIDCardNumber
                        />
                    </StyledCardContent>
                </StyledCard>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        marginTop: '20px',
                    }}
                >
                    <OutlinedButton $width="96px" $height="32px" onClick={handleAccountReset}>
                        Reset
                    </OutlinedButton>
                    <ContainedButton
                        $width="96px"
                        $height="32px"
                        style={{ marginLeft: '15px' }}
                        onClick={handleAccountSave}
                    >
                        Save
                    </ContainedButton>
                </div>
            </div>
        </div>
    );
};

EditProfileCard.propTypes = {
    mdUp: PropTypes.bool,
    onGoBack: PropTypes.func,
};
